// Sprint and masterplan of one frame (weeks and months side by side) read as two parts: a joint
// between them and a tab above each part naming its range. Drawing and grabbing only – the frame
// layout, the cells and the bindings stay exactly as the engine computes them.
import type { ColKind, FrameSpec } from "./engine-api";
import { monthSpan, weekSpan } from "./iso";

/** Height of the section tabs above a frame (board px). */
export const SECTION_TAB = 34;
/** Width of the joint where the last week meets the first month (board px, half on each side). */
export const SECTION_JOINT = 10;
/** Height of the "Diese Woche" flag above this week's red frame (board px). */
export const THIS_WEEK_FLAG = 22;

export interface Section { kind: "sprint" | "masterplan"; x0: number; x1: number; label: string }

type Dated = Extract<ColKind, { kind: "week" } | { kind: "month" }>;
const isDated = (k: ColKind): k is Dated => k.kind === "week" || k.kind === "month";
const num = (k: Dated) => ({ year: k.value.year, n: k.kind === "week" ? k.value.week : k.value.month });

/** x offsets (from the frame's left edge) where a week column and a month column meet. */
export function frameJoints(f: FrameSpec): number[] {
  const out: number[] = [];
  let x = 0;
  f.cols.forEach((c, i) => {
    x += c.width;
    const a = c.kind.kind, b = f.cols[i + 1]?.kind.kind;
    if ((a === "week" && b === "month") || (a === "month" && b === "week")) out.push(x);
  });
  return out;
}

/** The runs of week and month columns as tabs ("Sprint · KW 43 – 46", "Masterplan · Dez 2026 –
 *  Feb 2027"), offsets from the frame's left edge; none unless the frame has weeks AND months. */
export function frameSections(f: FrameSpec): Section[] {
  const runs: { kind: "week" | "month"; x0: number; x1: number; from: { year: number; n: number }; to: { year: number; n: number } }[] = [];
  let x = 0, prev: string | null = null;
  for (const c of f.cols) {
    const k = c.kind;
    if (isDated(k)) {
      const last = runs[runs.length - 1];
      if (last && prev === k.kind) { last.x1 = x + c.width; last.to = num(k); }
      else runs.push({ kind: k.kind, x0: x, x1: x + c.width, from: num(k), to: num(k) });
    }
    prev = k.kind;
    x += c.width;
  }
  if (!runs.some((r) => r.kind === "week") || !runs.some((r) => r.kind === "month")) return [];
  const joints = frameJoints(f);
  const near = (v: number) => joints.some((j) => Math.abs(j - v) < 0.5);
  return runs.map((r) => ({
    kind: r.kind === "week" ? "sprint" as const : "masterplan" as const,
    x0: r.x0 + (near(r.x0) ? SECTION_JOINT / 2 : 0),
    x1: r.x1 - (near(r.x1) ? SECTION_JOINT / 2 : 0),
    label: r.kind === "week" ? `Sprint · ${weekSpan(r.from, r.to)}` : `Masterplan · ${monthSpan(r.from, r.to)}`,
  }));
}
